'use strict';

/**
 * Model for saving the settings for the list view
 *
 * @category   Model
 * @module     collection
 * @copyright  Copyright (c) 2014, COLIDO (http://www.colido.net)
 * @author     Stefan Rueschenberg <sr@humans.am>
 * @author     Jonas Girnatis <dermusterknabe@gmail.com>
 */

var Backbone = require('backbone');
var ModelLocal = require('base/Colido.ModelLocal');

var SettingsObjectListModel = ModelLocal.extend({
    defaults: {
        id: 1,
        viewModeDefault: 'grid',
        sortGrid: 'modifiedTime.desc',
        sortList: 'modifiedTime.desc',
    },

    syncedAttrs: [
        'viewModeDefault',
        'sortGrid',
        'sortList',
    ],

    basePath: '/catalogs',

    catalog: null,

    /**
     * Name of the storage to use
     * @type {string|null}
     */
    storageKey: 'module:collection:list',

    initialize: function(attributes, options) {
        ModelLocal.prototype.initialize.apply(this, arguments);

        this.catalog = attributes && attributes.catalog ? attributes.catalog : null;

        if (this.catalog) {
            this.defaults.sortGrid = this.catalog.get('fieldsDefaultSort');
            this.defaults.sortList = this.catalog.get('fieldsDefaultSort');
        }

        // Set viewMode to last used viewMode (list or grid) in default mode
        this.set('viewMode', this.get('viewModeDefault'), {silent: true});

        this.on('change:sortGrid', function(that, sort) {
            if (this.get('viewMode') === 'grid') {
                that.set('sort', sort);
            }
        });

        this.on('change:sortList', function(that, sort) {
            if (this.get('viewMode') === 'list') {
                that.set('sort', sort);
            }
        });

        this.on('change:sort', function(that, sort) {
            if (this.get('viewMode') === 'list') {
                that.set('sortList', sort, {silent: true});
            } else if (this.get('viewMode') === 'grid') {
                that.set('sortGrid', sort, {silent: true});
            }
        });

        this.on('change:sort change:filter', function() {
            App.router.navigate(this.getPath(this.get('activeModel')), {trigger: false});
        }.bind(this));

        this.on('change:search', function() {
            App.router.navigate(this.getPath(this.get('activeModel')), {trigger: true});
        }.bind(this));
    },

    toJSON: function(options) {
        return _.pick(this.attributes, this.syncedAttrs);
    },

    getPath: function (target, options) {
        var parts = [this.basePath];

        var attrs = _.extend({}, this.attributes, options);

        var catalogId = (this.catalog && this.catalog.id) ? this.catalog.id : App.CollectionModule.catalogs.getActiveCatalog().id;

        parts.push(catalogId);

        if (attrs.search && _.isString(attrs.search)) {
            parts.push('search', attrs.search);
        }

        if (attrs.scanners) {
            parts.push('scanners');
        } else if (attrs.setModel) {
            parts.push('sets');
            // Setting 'set' to `true` means that list of sets should be shown
            if (attrs.setModel instanceof Backbone.Model) {
                parts.push(attrs.setModel.id);
            }
        } else if (attrs.locationModel) {
            parts.push('storages');
            // Setting 'location' to `true` means that list of locations should be shown
            if (attrs.locationModel instanceof Backbone.Model) {
                parts.push(attrs.locationModel.id);
            }
        } else if (attrs.importModel) {
            parts.push('imports');
            if (attrs.importModel instanceof Backbone.Model) {
                parts.push(attrs.importModel.id);
            }
        } else if (attrs.importWarningsModel) {
            parts.push('imports');
            parts.push('warnings');
            if (attrs.importWarningsModel instanceof Backbone.Model) {
                parts.push(attrs.importWarningsModel.id);
            }
        }

        if (attrs.filter) {
            parts.push('filter', attrs.filter);
        }

        if (attrs.sort) {
            if (attrs.viewMode === 'grid' && attrs.sort !== this.defaults.sortGrid || attrs.viewMode === 'list' && attrs.sort !== this.defaults.sortList) {
                parts.push('sort', attrs.sort);
            }
        }

        if (target != null) {
            if (target instanceof Backbone.Model) {
                target = target.id
            }
            parts.push('show', target);
        }

        return parts.join('/')
    },

    /**
     * Overrides default set method to ensure incompatible states are not
     * used concurrently
     */
    set: function (key, val, options) {
        // Handle both `"key", value` and `{key: value}` -style arguments.
        var attrs;
        if (typeof key === 'object') {
            attrs = key;
            options = val;
        } else {
            (attrs = {})[key] = val;
        }

        // Ensure no item is active when 'set' overview is shown
        if (attrs.setModel === true) {
            attrs.activeModel = null;
        }

        // Ensure 'set', 'location' and 'search' are not active concurrently
        if (attrs.setModel != null) {
            attrs.search = null;
            attrs.locationModel = null;
            attrs.importModel = null;
            attrs.importWarningsModel = null;
        } else if (attrs.locationModel != null) {
            attrs.search = null;
            attrs.setModel = null;
            attrs.importModel = null;
            attrs.importWarningsModel = null;
        } else if (attrs.importModel != null) {
            attrs.search = null;
            attrs.setModel = null;
            attrs.locationModel = null;
            attrs.importWarningsModel = null;
        } else if (attrs.importWarningsModel != null) {
            attrs.search = null;
            attrs.setModel = null;
            attrs.locationModel = null;
            attrs.locationModel = null;
        } else if (attrs.search) {
            attrs.setModel = null;
            attrs.locationModel = null;
            attrs.importModel = null;
            attrs.importWarningsModel = null;
        }

        // Remove current filter when 'set', 'location' or 'search' changes
        if (!attrs.filter
            && !(_.isUndefined(attrs.setModel)
            && _.isUndefined(attrs.locationModel)
            && _.isUndefined(attrs.importModel)
            && _.isUndefined(attrs.importWarningsModel)
            && _.isUndefined(attrs.search))
        ) {
            attrs.filter = null;
        }

        // Quit manage mode when any filtering-relevant attribute changes
        if (_.any([
                'search',
                'filter',
                'setModel',
                'locationModel',
                'importModel',
                'importWarningsModel',
            ], function (attr) { return !_.isUndefined(attrs[attr]) })) {
            attrs.controlMode = 'default';
        }

        // Ensure 'sort' values are valid and saved for the current viewMode
        var viewMode = attrs.viewMode || this.get('viewMode');

        if (viewMode === 'list') {
            if (!attrs.sort) {
                attrs.sort = attrs.sortList || this.get('sortList') || this.defaults.sortList;
            }
            attrs.sortList = attrs.sort;
        } else if (viewMode === 'grid') {
            if (!attrs.sort) {
                attrs.sort = attrs.sortGrid || this.get('sortGrid') || this.defaults.sortGrid;
            }

            // if (!_.contains(this.validSortGridValues, attrs.sort)) {
            //     attrs.sort = this.defaults.sortGrid;
            // }
            attrs.sortGrid = attrs.sort;
        }

        ModelLocal.prototype.set.call(this, attrs, options);

        if (this.localStorage) {
            this.save();
        }
    },

    // validSortGridValues: [
    //     'name.asc',
    //     'objectId.asc',
    //     'locality.asc',
    //     'lastEdited.desc',
    // ],
});

module.exports = SettingsObjectListModel;